import React from 'react';
import PropTypes from 'prop-types';
import { ChevronLeft } from 'lucide-react';
import SectionHeader from './SectionHeader';
import AuraLightBackground from './AuraLightBackground';

/**
 * PageShell des Design-Systems (siehe MODERNIZATION_PLAN.md P3.3).
 *
 * Vereinheitlicht das Seiten-Gerüst, das vorher in ~10 Settings-/Detail-Seiten
 * fast wortgleich kopiert war (sticky Header mit Zurück-Button + zentriertem
 * Titel + Spacer, darunter ein max-Breite-Content-Wrapper) - jede Kopie mit
 * eigenen kleinen Abweichungen bei Padding/Hintergrund (das "Padding-Chaos"
 * aus dem Plan-Audit).
 *
 * ZWEI GERÜSTE, weil die App zwei Sorten Seiten hat (August 2026):
 *
 * - variant="detail" (Standard, unverändertes Verhalten): Unterseiten, die man
 *   wieder verlässt. Sticky Kopf mit Zurück-Pfeil und zentriertem Titel.
 *   <PageShell title="Passwort ändern" onBack={() => navigate('/account-settings')}>
 *
 * - variant="main": Hauptseiten der Bottom-Navigation. Kein Zurück-Pfeil, dafür
 *   Titel + Zähler links und die Primäraktion rechts. Genau dieses Muster
 *   hatten Sessions, Logbuch und Matches je einzeln nachgebaut — deshalb sahen
 *   ihre Köpfe verschieden aus. Rendert intern `ui/SectionHeader.jsx`.
 *   Seit Oktober 2026 ohne Icon vor dem Titel (Bungee ist der Anker).
 *   <PageShell variant="main" title="Lernsessions"
 *              meta="0 aktiv · 0 geplant" actions={<Button size="sm">Neue Session</Button>}>
 *
 * `embedded` für Ansichten, die MainScreen bereits rahmt (Bottom-Nav, Padding,
 * Hintergrund): dann liefert PageShell nur den Kopf, keine eigene Seitenhülle.
 *
 * `background` zeichnet den Aura-Verlauf. Bewusst NICHT der Standard: Wer schon
 * in MainScreen läuft, bekommt ihn von dort — zwei Schleier übereinander addieren
 * ihre Deckkraft und lassen die Ansicht heller wirken als den Rest der App
 * (siehe MIGRATION_LOG.md, "Chat-Liste war heller als der Rest", 12.08.2026).
 */
/**
 * Zwei Breiten-ROLLEN statt sieben technischer Groessen (August 2026).
 *
 * Vorher reichte die Spanne von 672px („Neue Session") bis 1280px (Logbuch in
 * der Hauptansicht) — dieselbe Ansicht war je nach Einstiegsweg unterschiedlich
 * breit. Wer eine Seite baut, soll nicht ueber Pixel nachdenken, sondern ueber
 * die Frage: Liste oder Formular?
 *
 *   list — Uebersichten, Listen, Kartenraster. Schmal wirken sie verloren.
 *   form — Formulare, Detail- und Einstellungsseiten. Lange Eingabezeilen sind
 *          am Desktop schwer zu lesen; ~768px ist die uebliche Lesebreite.
 */
const WIDTH_ROLES = {
  list: 'max-w-6xl',
  form: 'max-w-3xl',
};

const PageShell = React.forwardRef(({
  variant = 'detail',
  title,
  onBack,
  backLabel = 'Zurück',
  rightSlot = null,
  icon,
  meta,
  // Nur variant="main": haelt `meta` auch auf dem Telefon in der Titelzeile
  // (siehe ui/SectionHeader.jsx). Fuer kurze Zaehler gedacht.
  metaInline = false,
  // Nur variant="main": Seitentitel in der Display-Schrift (Bungee, Versalien).
  //
  // STANDARD AN, seit August 2026. Die Hauptseiten heissen "Entdecken",
  // "Lernsessions", "Logbuch", "Chats" — ein Wort, einmal pro Ansicht, an der
  // einzigen Stelle, wo Platz fuer Charakter ist. Bis dahin galt "Bungee nur
  // fuer Marketing"; das war eine Vorsicht zu viel, die Display-Schrift IST
  // die Marke und hat in der App nichts verloren zu suchen.
  //
  // Abschalten mit `display={false}` — noetig, sobald ein Titel laenger als
  // zwei Woerter ist oder aus Nutzerdaten kommt (Namen in Versalien lesen sich
  // wie Geschrei). Detailseiten sind gar nicht betroffen: Deren Kopf ist eine
  // Zeile Poppins in einer sticky Leiste und bleibt es.
  display = true,
  description,
  actions,
  width = 'form',
  contentClassName = '',
  embedded = false,
  background = false,
  fullscreen = false,
  bottomBar = false,
  headerExtra = null,
  // Kein eigener Seitengrund und ein Glas-Kopf: fuer Seiten, die ihren
  // Hintergrund selbst malen (die Fokus-Session mit der Aura-Szene dahinter).
  transparent = false,
  children,
  // Rest landet auf dem aeusseren Container (z. B. Event-Handler oder
  // data-Attribute). Aus demselben Grund nimmt die Komponente einen ref
  // entgegen — er zeigt bei Fullscreen-Seiten auf den Scroll-Container.
  ...rest
}, ref) => {
  // Seit August 2026 gibt es nur noch die zwei Rollen. Die alte,
  // groessenbasierte `maxWidth`-Prop ist entfallen, nachdem die letzten sechs
  // Aufrufe uebersetzt waren — sonst waere der Rueckfallweg still der
  // bequemere geblieben. `form` ist der Standard: die meisten Seiten sind
  // Formulare oder Lesetexte, Listen sagen es ausdruecklich.
  const widthClass = WIDTH_ROLES[width] || WIDTH_ROLES.form;

  // Der Abstand am Seitenende haengt an genau einer Frage: Liegt unten eine
  // fixierte Leiste — die Bottom-Navigation oder ein Aktionsfooter? Eigene
  // Routen (alles ausserhalb von /app) haben keine; die dort bis August 2026
  // reservierten 8rem waren ein leeres Feld unter dem Inhalt.
  const bottomPadding = bottomBar
    ? 'var(--page-bottom-nav)'
    : 'var(--page-bottom-plain)';
  // Fullscreen-Seiten der nativen App (MOBILE_UX_GUIDE: Fullscreen Pages statt
  // Modals) legen sich ueber alles und scrollen in sich selbst.
  // overflow-x-clip statt overflow-x-hidden: hidden macht den Container zu
  // einem neuen Scroll-Container (overflow-y springt sonst stillschweigend auf
  // auto hoch) und bricht damit den sticky Kopf weiter unten - clip schneidet
  // nur das seitliche Ueberlaufen ab, ohne die Scroll-Achsen neu zu verdrahten.
  // Ohne diese Zeile liess sich die Seite auf dem Handy seitlich wegscrollen,
  // sobald ein Kind (z.B. ein zu breiter Platzhaltertext) minimal ueberstand.
  const outerClass = fullscreen
    ? 'fixed inset-0 bg-surface-raised overflow-y-auto overflow-x-clip'
    : `min-h-screen relative overflow-x-clip${transparent ? '' : ' bg-background'}`;
  const headerSurface = transparent ? 'bg-surface/70 backdrop-blur-md' : 'bg-surface';

  // ── variant="main" ──────────────────────────────────────────────────────
  if (variant === 'main') {
    const backButton = onBack ? (
      <button
        type="button"
        onClick={onBack}
        aria-label={backLabel}
        className="p-2 -ml-2 hover:bg-foreground/5 rounded-xl transition-colors min-h-[44px] min-w-[44px] flex items-center justify-center flex-shrink-0 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/50"
      >
        <ChevronLeft className="h-6 w-6 text-muted-foreground" />
      </button>
    ) : null;

    // Mit Zurück-Knopf: min-h wie der Knopf (44 px), sonst sitzt der kompakte
    // Titel oben über dem Pfeil statt auf seiner Höhe.
    const sectionHeader = (
      <SectionHeader
        as="h1"
        compact
        display={display}
        icon={icon}
        title={title}
        meta={meta}
        metaInline={metaInline}
        description={description}
        actions={actions}
        className={backButton ? 'flex flex-1 min-w-0 min-h-[44px] flex-col justify-center' : 'mb-4'}
      />
    );

    // Eigene Route (z.B. /sessions) braucht einen Rueckweg; in MainScreen
    // eingebettet gibt es keinen — dort ist die Bottom-Navigation der Weg zurueck.
    const heading = backButton ? (
      <div className="flex items-start gap-1 mb-4">
        {backButton}
        {sectionHeader}
      </div>
    ) : (
      sectionHeader
    );

    if (embedded) {
      return (
        <>
          {heading}
          {children}
        </>
      );
    }

    // Derselbe aeussere Rahmen wie variant="detail" — vorher stand hier
    // `min-h-screen` fest verdrahtet, weshalb `fullscreen` in dieser Variante
    // stillschweigend wirkungslos war.
    //
    // Safe-Area oben braucht es hier nicht: Die globale Regel in index.css
    // (`html.native-app .min-h-screen`) polstert den aeusseren Container in der
    // nativen App bereits. Ein zweites env() an dieser Stelle addierte sich.
    return (
      <div ref={ref} className={outerClass} {...rest}>
        {background && <AuraLightBackground intensity="normal" />}
        <div
          className={`${widthClass} mx-auto px-4 pt-6 relative ${contentClassName}`}
          style={{ paddingBottom: bottomPadding }}
        >
          {heading}
          {children}
        </div>
      </div>
    );
  }

  // ── variant="detail" ────────────────────────────────────────────────────
  if (embedded) {
    return (
      <>
        <div className="flex items-center justify-between gap-2 mb-4">
          {onBack ? (
            <button
              type="button"
              onClick={onBack}
              aria-label={backLabel}
              className="p-2 hover:bg-foreground/5 rounded-xl transition-colors min-h-[44px] min-w-[44px] flex items-center justify-center focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/50"
            >
              <ChevronLeft className="h-6 w-6 text-muted-foreground" />
            </button>
          ) : (
            <div className="w-10 shrink-0" />
          )}
          <h1 className="text-lg font-semibold text-foreground truncate">{title}</h1>
          {rightSlot || <div className="w-10 shrink-0" />}
        </div>
        {children}
      </>
    );
  }

  return (
    <div ref={ref} className={outerClass} {...rest}>
      {background && <AuraLightBackground intensity="normal" />}
      {/* Safe-Area immer beruecksichtigen: auf dem Web ist env() = 0, in der
          nativen App schiebt es den Kopf unter Notch/Statusleiste. Vorher hat das
          jede Fullscreen-Seite selbst gesetzt — oder eben vergessen. */}
      <div
        className={`sticky top-0 z-40 ${headerSurface} border-b border-border px-4 py-4`}
        style={{ paddingTop: 'calc(env(safe-area-inset-top, 0px) + 1rem)' }}
      >
        <div className={`${widthClass} mx-auto`}>
          <div className="flex items-center justify-between gap-2">
            {onBack ? (
              <button
                type="button"
                onClick={onBack}
                aria-label={backLabel}
                className="p-2 hover:bg-foreground/5 rounded-xl transition-colors min-h-[44px] min-w-[44px] flex items-center justify-center focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/50"
              >
                <ChevronLeft className="h-6 w-6 text-muted-foreground" />
              </button>
            ) : (
              <div className="w-10 shrink-0" />
            )}
            <h1 className="text-lg font-semibold text-foreground truncate">{title}</h1>
            {rightSlot || <div className="w-10 shrink-0" />}
          </div>
        </div>
        {/* z.B. eine Filter-Leiste, die mit dem Kopf oben kleben soll */}
        {headerExtra}
      </div>

      <div
        className={`${widthClass} mx-auto px-4 pt-6 relative ${contentClassName}`}
        style={{ paddingBottom: bottomPadding }}
      >
        {children}
      </div>
    </div>
  );
});

PageShell.displayName = 'PageShell';

PageShell.propTypes = {
  variant: PropTypes.oneOf(['detail', 'main']),
  title: PropTypes.node.isRequired,
  onBack: PropTypes.func,
  backLabel: PropTypes.string,
  rightSlot: PropTypes.node,
  /** deprecated (Okt 2026): Titel stehen ohne Icon. Bleibt nur für Altbestand, nicht mehr übergeben */
  icon: PropTypes.elementType,
  /** nur variant="main": kleiner Zähler neben dem Titel ("0 aktiv · 0 geplant") */
  meta: PropTypes.node,
  /** nur variant="main": Zähler auch auf dem Telefon in der Titelzeile ("Entdecken · 1 von 3") */
  metaInline: PropTypes.bool,
  /** nur variant="main": Titel in Bungee-Versalien (Standard an; aus für lange oder aus Daten kommende Titel) */
  display: PropTypes.bool,
  /** nur variant="main": erklärender Satz unter dem Titel */
  description: PropTypes.node,
  /** nur variant="main": Primäraktion(en) rechts im Kopf */
  actions: PropTypes.node,
  /** Breiten-Rolle: 'list' (Übersichten, 1152px) oder 'form' (Formulare, 768px) */
  width: PropTypes.oneOf(Object.keys(WIDTH_ROLES)),
  contentClassName: PropTypes.string,
  /** Ansicht läuft in MainScreen — nur den Kopf rendern, keine Seitenhülle */
  embedded: PropTypes.bool,
  /** Aura-Verlauf zeichnen (nur für eigene Routen, nie zusätzlich zu MainScreen) */
  background: PropTypes.bool,
  /** Fullscreen-Seite der nativen App: liegt über allem und scrollt in sich */
  fullscreen: PropTypes.bool,
  /** Unter der Seite liegt eine fixierte Leiste (Bottom-Nav oder Aktionsfooter) */
  bottomBar: PropTypes.bool,
  /** Inhalt, der im sticky Kopf unter der Titelzeile klebt (z.B. Filter-Tabs) */
  headerExtra: PropTypes.node,
  /** Seite malt ihren Grund selbst: kein bg-background, Kopf als Glas (nur variant="detail") */
  transparent: PropTypes.bool,
  children: PropTypes.node,
};

export default PageShell;
